// Company register for platform administrators: list, add, activate, suspend.
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NativeSelect,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { FormError, Notice } from "../../shared/ui/Page";
import { useStepUp } from "../../shared/session/stepup";
import { AdminLayout } from "./AdminLayout";
import { useRequested } from "./approvals";

type Company = Schemas["Company"];
type Status = Company["status"];
type NewCompany = Schemas["NewCompany"];
type StaffRole = Schemas["StaffRole"];

const SEGMENTS: NewCompany["segment"][] = [
  "bank",
  "sacco",
  "fintech",
  "telco",
  "retail",
  "logistics",
  "government",
  "ngo",
  "other",
];
const STATUS_COLOUR: Record<Status, string> = { pending: "yellow", active: "green", suspended: "red" };

export function AdminCompaniesPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")}>
      <Companies />
    </AdminLayout>
  );
}

function Companies() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Status | "all">("all");
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState<Company | null>(null);
  const companies = useQuery({
    queryKey: ["admin", "companies", filter],
    queryFn: () =>
      unwrap(
        api.GET("/admin/companies", {
          params: { query: { limit: 200, ...(filter === "all" ? {} : { status: filter }) } },
        }),
      ),
  });
  const items = companies.data?.items ?? [];

  const setStatus = async (c: Company, status: Status) => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.POST("/admin/companies/{company_id}/status", {
      params: { path: { company_id: c.id } },
      body: { status },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setNotice(
      t("admin_companies.status_changed", { name: data.name, status: t(`admin_companies.status_${data.status}`) }),
    );
    void queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
  };

  // A rename reaches texts only once approved (SR-2026-10-28-06).
  const approveName = async (c: Company) => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.POST("/admin/companies/{company_id}/text-name", {
      params: { path: { company_id: c.id } },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("admin_companies.text_name_approved", { name: data.name }));
    void queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
  };

  return (
    <Stack gap="md">
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {adding ? (
        <CreateCompany
          onCancel={() => setAdding(false)}
          onCreated={(c) => {
            setAdding(false);
            setNotice(t("admin_companies.created", { name: c.name }));
            void queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
          }}
        />
      ) : (
        <Button w="fit-content" onClick={() => setAdding(true)}>
          {t("admin_companies.add")}
        </Button>
      )}
      <Group gap="sm">
        <Text size="sm" id="company-filter">
          {t("admin_companies.filter")}
        </Text>
        <SegmentedControl
          size="xs"
          aria-labelledby="company-filter"
          value={filter}
          onChange={(v) => setFilter(v as Status | "all")}
          data={[
            { value: "all", label: t("admin_companies.all") },
            { value: "pending", label: t("admin_companies.status_pending") },
            { value: "active", label: t("admin_companies.status_active") },
            { value: "suspended", label: t("admin_companies.status_suspended") },
          ]}
        />
      </Group>
      {companies.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : companies.isError ? (
        <FormError message={errorMessage(companies.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed">{t("admin_companies.empty")}</Text>
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("admin_companies.name")}</Table.Th>
                <Table.Th>{t("admin_companies.segment")}</Table.Th>
                <Table.Th>{t("admin_companies.contact")}</Table.Th>
                <Table.Th>{t("admin_companies.status")}</Table.Th>
                <Table.Th>{t("admin_companies.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((c) => (
                <Table.Tr key={c.id} data-testid="company-row">
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {c.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {c.public_ref}
                    </Text>
                    {c.text_name && c.text_name !== c.name && (
                      <Text size="xs" c="orange" data-testid="text-name">
                        {t("admin_companies.text_name", { name: c.text_name })}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{t(`segment.${c.segment}`, { defaultValue: c.segment })}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{c.contact_name}</Text>
                    <Text size="xs" c="dimmed">
                      {c.contact_email}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOUR[c.status]}>{t(`admin_companies.status_${c.status}`)}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      {c.status === "active" ? (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => void setStatus(c, "suspended")}
                          aria-label={t("admin_companies.suspend_for", { name: c.name })}
                        >
                          {t("admin_companies.suspend")}
                        </Button>
                      ) : (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => void setStatus(c, "active")}
                          aria-label={t("admin_companies.activate_for", { name: c.name })}
                        >
                          {t("admin_companies.activate")}
                        </Button>
                      )}
                      {c.text_name && c.text_name !== c.name && (
                        <Button
                          size="xs"
                          variant="light"
                          color="orange"
                          onClick={() => void approveName(c)}
                          aria-label={t("admin_companies.approve_name_for", { name: c.name })}
                        >
                          {t("admin_companies.approve_name")}
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => setInviting(c)}
                        aria-label={t("admin_invite.invite_for", { name: c.name })}
                      >
                        {t("admin_invite.invite")}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <InviteCompanyAdmin
        company={inviting}
        onClose={() => setInviting(null)}
        onInvited={(c, email) => {
          setInviting(null);
          setError(null);
          setNotice(t("admin_invite.requested", { email, name: c.name }));
        }}
      />
    </Stack>
  );
}

/** InviteCompanyAdmin emails a company's first staff member a single-use invitation (FR-41). */
function InviteCompanyAdmin({
  company,
  onClose,
  onInvited,
}: {
  company: Company | null;
  onClose: () => void;
  onInvited: (c: Company, email: string) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole>("company_admin");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const stepUp = useStepUp();
  const requested = useRequested();

  const close = () => {
    setEmail("");
    setRole("company_admin");
    setError(null);
    onClose();
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!company) return;
    setError(null);
    // A second administrator approves before the invitation is sent (ADR 0041).
    const token = await stepUp("admin_change");
    if (!token) return;
    setBusy(true);
    const { error, response } = await api.POST("/admin/companies/{company_id}/invitations", {
      params: { path: { company_id: company.id }, header: { "X-Step-Up-Token": token } },
      body: { email: email.trim(), role },
    });
    setBusy(false);
    if (response.status !== 202) return setError(errorMessage(error, t("error.generic")));
    requested();
    const sentTo = email.trim();
    setEmail("");
    setRole("company_admin");
    onInvited(company, sentTo);
  };

  return (
    <Modal
      opened={company !== null}
      onClose={close}
      title={t("admin_invite.title", { name: company?.name ?? "" })}
      centered
      closeButtonProps={{ "aria-label": t("common.close") }}
    >
      <form onSubmit={submit} data-testid="admin-invite-form">
        <Stack gap="sm">
          <Text size="sm">{t("admin_invite.intro")}</Text>
          <TextInput
            type="email"
            label={t("admin_invite.email")}
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
            data-autofocus
          />
          <NativeSelect
            label={t("admin_invite.role")}
            value={role}
            onChange={(e) => setRole(e.currentTarget.value as StaffRole)}
            data={[
              { value: "company_admin", label: t("staff_role.company_admin") },
              { value: "company_viewer", label: t("staff_role.company_viewer") },
            ]}
          />
          <FormError message={error} />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy}>
              {t("admin_invite.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function CreateCompany({ onCancel, onCreated }: { onCancel: () => void; onCreated: (c: Company) => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    segment: "bank" as NewCompany["segment"],
    registration_number: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    status: "pending" as NewCompany["status"],
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.currentTarget.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const body: NewCompany = {
      name: form.name.trim(),
      segment: form.segment,
      contact_name: form.contact_name.trim(),
      contact_email: form.contact_email.trim(),
      status: form.status,
      ...(form.registration_number.trim() ? { registration_number: form.registration_number.trim() } : {}),
      ...(form.contact_phone.trim() ? { contact_phone: form.contact_phone.trim() } : {}),
    };
    const { data, error } = await api.POST("/admin/companies", { body });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    onCreated(data);
  };

  return (
    <Paper withBorder p="md" radius="md">
      <form onSubmit={submit}>
        <Stack gap="sm">
          <Title order={2} size="h4">
            {t("admin_companies.add")}
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label={t("admin_companies.name")} value={form.name} onChange={set("name")} required />
            <NativeSelect
              label={t("admin_companies.segment")}
              value={form.segment}
              onChange={set("segment")}
              data={SEGMENTS.map((s) => ({ value: s, label: t(`segment.${s}`) }))}
            />
            <TextInput
              label={t("admin_companies.registration")}
              value={form.registration_number}
              onChange={set("registration_number")}
            />
            <TextInput
              label={t("admin_companies.contact_name")}
              value={form.contact_name}
              onChange={set("contact_name")}
              required
            />
            <TextInput
              type="email"
              label={t("admin_companies.contact_email")}
              value={form.contact_email}
              onChange={set("contact_email")}
              required
            />
            <TextInput
              type="tel"
              label={t("admin_companies.contact_phone")}
              value={form.contact_phone}
              onChange={set("contact_phone")}
            />
            <NativeSelect
              label={t("admin_companies.initial_status")}
              value={form.status}
              onChange={set("status")}
              data={[
                { value: "pending", label: t("admin_companies.status_pending") },
                { value: "active", label: t("admin_companies.status_active") },
              ]}
            />
          </SimpleGrid>
          <FormError message={error} />
          <Group gap="xs">
            <Button type="submit" loading={busy}>
              {t("admin_companies.create")}
            </Button>
            <Button variant="default" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
}
